import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  forwardRef,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { NG_VALIDATORS, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgbDateParserFormatter } from "@ng-bootstrap/ng-bootstrap";
import { NavController, NavParams } from "ionic-angular";
import { from } from "rxjs/observable/from";
import { of } from "rxjs/observable/of";
import { catchError, map, mergeMap, toArray } from "rxjs/operators";
import { Subscription } from "rxjs/Subscription";
import { PayersService } from "../../../providers/payers-service/payers.service";
import { StateManagementService } from "../../../providers/state-management/state-management.service";
import { NgbCustomParserFormatter } from "../../common/date-picker/ngb-custom-parser-formatter";

@Component({
  selector: "rates-payer",
  templateUrl: "./rates-payer.html",
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => RatesPayer),
      multi: true,
    },
    {
      provide: NG_VALIDATORS,
      useExisting: forwardRef(() => RatesPayer),
      multi: true,
    },
    { provide: NgbDateParserFormatter, useClass: NgbCustomParserFormatter },
  ],
  changeDetection: ChangeDetectionStrategy.Default,
})
export class RatesPayer implements OnInit, OnDestroy {
  @Input() currentPayer: any;
  @Input() currentBenefitType: any;
  @Input() currentPayerType: any;
  @Input() isEditMode: boolean = false;
  @Output() onTouched = new EventEmitter<boolean>();
  @Output() onRatesSaved = new EventEmitter<any>();
  @Output() onCanceled = new EventEmitter<any>();

  benefitType: string[];
  payerType: string;
  touched: boolean;
  callRatesPayer = false;
  // CO-3334 (epic CO-1390): while OFF, Medicare payers keep today's
  // behavior (no Hospice Visits tab). ON extends the tab to Medicare
  // hospice payers; Hospice LOC stays non-Medicare either way (Medicare
  // per-diem LOC rates come from CMS/CBSA loading, not payer config).
  medicareHospiceVisitBillingToggle: boolean = false;
  private medicareVisitBillingSub: Subscription;

  tabs = [
    {
      id: "hospice-loc",
      title: "Hospice LOC",
      selected: false,
      icon: "md-radio-button-off",
      visible: false,
      benefitType: "HOSPICE",
      benefitSubtype: "LOC",
    },
    {
      id: "hospice-visits",
      title: "Hospice Visits",
      selected: false,
      icon: "md-radio-button-off",
      visible: false,
      benefitType: "HOSPICE",
      benefitSubtype: "VISITS",
    },
    {
      id: "palliative-visits",
      title: "Palliative",
      selected: false,
      icon: "md-radio-button-off",
      visible: false,
      benefitType: "PALLIATIVE",
      benefitSubtype: null,
    },
    {
      id: "room-and-board",
      title: "Room and Board",
      selected: false,
      icon: "md-radio-button-off",
      visible: false,
      benefitType: "ROOMANDBOARD",
      benefitSubtype: "VISITS",
    },
  ];

  activeTab = "";
  activeTabId = "";

  constructor(
    protected navParams: NavParams,
    protected navCtrl: NavController,
    protected payersService: PayersService,
    protected stateManagementService: StateManagementService
  ) {}

  ngOnInit(): void {
    this.medicareVisitBillingSub =
      this.stateManagementService.medicareHospiceVisitBillingToggle.subscribe(
        (enabled) => {
          const changed = this.medicareHospiceVisitBillingToggle != !!enabled;
          this.medicareHospiceVisitBillingToggle = !!enabled;
          if (changed) this.refreshTabVisibility();
        }
      );
    if (this.callRatesPayer == false) {
      this.tabSelection();
      if (this.currentBenefitType)
        this.validateCheckmarkInfo().then((result) => {
          if (result) this.onRatesSaved.emit();
        });
    }
  }

  // Re-derives tab visibility through whichever path applies to how this
  // component instance is driven (init-time payer object vs ngOnChanges
  // inputs), so a late-arriving feature-toggle emission is reflected.
  private refreshTabVisibility() {
    this.tabSelection();
    if (
      this.callRatesPayer &&
      this.benefitType &&
      this.benefitType.length > 0 &&
      this.payerType &&
      this.payerType.length > 0
    ) {
      this.checkTabVisibility();
    }
  }

  private validateCheckmarkInfo() {
    return new Promise((resolve, reject) => {
      const apiCalls = [];
      for (let tab of this.tabs) {
        apiCalls.push(
          this.payersService.getPayerRatesPeriods(
            this.currentPayer,
            tab.benefitType,
            tab.benefitSubtype
          )
        );
      }
      from(apiCalls)
        .pipe(
          mergeMap((apiCall, index) => {
            return apiCall.pipe(
              catchError((error) => {
                return of(null);
              })
            );
          }),
          toArray(),
          map((results) => {
            // @ts-ignore
            const hasValidResult = results.some(
              (result) =>
                // @ts-ignore
                result !== null && result.length > 0
            );

            resolve(hasValidResult);
            return hasValidResult;
          })
        )
        .subscribe({
          error: (error) => reject(error),
          complete: () => {},
        });
    });
  }

  tabSelection() {
    if (this.currentPayer && this.currentPayer.benefitType) {
      this.benefitType = this.currentPayer.benefitType.split(",");
      this.payerType = this.currentPayer.payerType;
      this.benefitType.forEach((type) => {
        if (type.toLowerCase().trim() == "roomandboard") {
          this.tabs[3].visible = true;
        } else if (type.toLowerCase() == "palliative") {
          this.tabs[2].visible = true;
        } else if (type.toLowerCase() == "hospice") {
          const isMedicare = this.payerType.toUpperCase() == "MEDICARE";
          if (!isMedicare) {
            this.tabs[0].visible = true;
            this.tabs[1].visible = true;
          } else if (this.medicareHospiceVisitBillingToggle) {
            this.tabs[1].visible = true;
          }
        }
      });
    }
  }

  getVisibleTabs() {
    return this.tabs.filter((tab) => tab.visible);
  }

  ngOnDestroy(): void {
    if (this.medicareVisitBillingSub) {
      this.medicareVisitBillingSub.unsubscribe();
    }
  }

  ngOnChanges() {
    this.payerType = this.currentPayerType;
    this.callRatesPayer = true;
    this.benefitType = this.currentBenefitType;
    this.tabSelection();
    if (
      this.benefitType &&
      this.benefitType.length > 0 &&
      this.payerType &&
      this.payerType.length > 0
    ) {
      this.checkTabVisibility();
    }
  }

  selectTab(selectedTab) {
    // update tabs
    this.tabs = this.tabs.map((tab) => {
      tab.selected = tab.id === selectedTab.id;
      return tab;
    });

    // update activeTab key for sections displayed
    this.activeTab = selectedTab;
    this.activeTabId = selectedTab.id;
  }

  close() {
    this.onCanceled.emit(true);
  }

  ratesSaved() {
    this.onRatesSaved.emit();
  }

  checkTabVisibility() {
    this.tabs = this.tabs.map((tab) => {
      switch (tab.id) {
        case "hospice-loc":
          tab.visible =
            this.currentBenefitType &&
            this.currentBenefitType.length > 0 &&
            this.currentBenefitType.includes("HOSPICE");
          break;
        case "hospice-visits":
          tab.visible =
            this.currentBenefitType &&
            this.currentBenefitType.length > 0 &&
            this.currentBenefitType.includes("HOSPICE") &&
            this.currentPayerType &&
            this.currentPayerType.length > 0 &&
            (this.currentPayerType.toUpperCase() != "MEDICARE" ||
              this.medicareHospiceVisitBillingToggle);
          break;
        case "palliative-visits":
          tab.visible =
            this.currentBenefitType &&
            this.currentBenefitType.length > 0 &&
            this.currentBenefitType.includes("PALLIATIVE");
          break;
        case "room-and-board":
          tab.visible =
            this.currentBenefitType &&
            this.currentBenefitType.length > 0 &&
            (this.currentBenefitType.includes("ROOM AND BOARD") ||
              this.currentBenefitType.includes("ROOMANDBOARD"));
          break;
        default:
          this.activeTab = "";
          this.activeTabId = "";
          break;
      }
      return tab;
    });
  }
}



// WEBPACK FOOTER //
// ./src/components/payers/rates-payer/rates-payer.ts